import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../../reducers/cart';
import { setProductListData } from '../../../reducers/productSlice';
import { fetch } from '../../../utils';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner, Offcanvas } from 'react-bootstrap';
import CartSidebar from '../../component/CartSidebar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SubCategory = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { category_slug, subcategory_slug } = useParams();

    const [productList, setProductList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cartOpen, setCartOpen] = useState(false);
    const [mobileFilter, setMobileFilter] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(null);
    const [sortOption, setSortOption] = useState('default');
    const [stockFilter, setStockFilter] = useState('all');
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(0);
    const [selectedMin, setSelectedMin] = useState(0);
    const [selectedMax, setSelectedMax] = useState(0);

    const tokenData = localStorage.getItem('muskan_token_data');
    const parsedTokenData = tokenData ? JSON.parse(tokenData) : null;

    const getPriceObj = product =>
        product?.prices?.find(p => Number(p?.product_MSP) > 0) ||
        product?.prices?.[0] ||
        {};

    const getPrice = product => Number(getPriceObj(product)?.product_MSP || 0);
    const getMRP = product => Number(getPriceObj(product)?.product_MRP || getPrice(product));

    const getStock = product =>
        String(product?.in_stock_status || '').trim().toLowerCase() === 'in stock';

    const getDiscount = (mrp, price) =>
        !mrp || !price || mrp <= price ? 0 : Math.round(((mrp - price) / mrp) * 100);

    const getImage = url => {
        if (!url) return '';
        const match = String(url).match(/\((.*?)\)/);
        return match ? match[1] : url;
    };

    const getProducts = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                '/product/get-products-by-category',
                'POST',
                {
                    category_slug,
                    sub_category_slug: subcategory_slug || null
                },
                { 'Content-Type': 'application/json' }
            );

            const data = response?.data?.data?.product_list || [];
            setProductList(data);
            dispatch(setProductListData(data));

            if (data.length) {
                const prices = data.map(getPrice);
                const min = Math.floor(Math.min(...prices));
                const max = Math.ceil(Math.max(...prices));
                setMinPrice(min);
                setMaxPrice(max);
                setSelectedMin(min);
                setSelectedMax(max);
            } else {
                setMinPrice(0);
                setMaxPrice(0);
                setSelectedMin(0);
                setSelectedMax(0);
            }

            setSelectedAttributes({});
            setStockFilter('all');
            setSortOption('default');
        } catch (error) {
            console.error(error);
            setProductList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getProducts();
    }, [category_slug, subcategory_slug]);

    const attributeGroups = useMemo(() => {
        const groups = {};

        productList.forEach(product => {
            (product?.attributes || []).forEach(attr => {
                if (!groups[attr.attribute_id]) {
                    groups[attr.attribute_id] = {
                        attribute_id: attr.attribute_id,
                        attribute_name: attr.attribute_name,
                        values: {}
                    };
                }

                groups[attr.attribute_id].values[attr.attribute_value_id] = {
                    attribute_value_id: attr.attribute_value_id,
                    attribute_value: attr.attribute_value
                };
            });
        });

        return Object.values(groups).map(group => ({
            ...group,
            values: Object.values(group.values)
        }));
    }, [productList]);

    const toggleAttribute = (attributeId, valueId) => {
        setSelectedAttributes(prev => {
            const current = prev[attributeId] || [];
            const exists = current.includes(valueId);

            return {
                ...prev,
                [attributeId]: exists
                    ? current.filter(id => id !== valueId)
                    : [...current, valueId]
            };
        });
    };

    const filteredProducts = useMemo(() => {
        let result = productList.filter(product => {
            const price = getPrice(product);

            if (price < selectedMin || price > selectedMax) return false;
            if (stockFilter === 'in' && !getStock(product)) return false;
            if (stockFilter === 'out' && getStock(product)) return false;

            for (const [attributeId, values] of Object.entries(selectedAttributes)) {
                if (!values?.length) continue;

                const productValueIds = (product?.attributes || [])
                    .filter(a => String(a.attribute_id) === String(attributeId))
                    .map(a => Number(a.attribute_value_id));

                if (!values.some(valueId => productValueIds.includes(Number(valueId)))) {
                    return false;
                }
            }

            return true;
        });

        if (sortOption === 'price-low-to-high')
            result.sort((a, b) => getPrice(a) - getPrice(b));
        else if (sortOption === 'price-high-to-low')
            result.sort((a, b) => getPrice(b) - getPrice(a));
        else if (sortOption === 'discount')
            result.sort(
                (a, b) =>
                    getDiscount(getMRP(b), getPrice(b)) -
                    getDiscount(getMRP(a), getPrice(a))
            );
        else if (sortOption === 'name-ascending')
            result.sort((a, b) =>
                String(a.product_name || '').localeCompare(String(b.product_name || ''))
            );
        else if (sortOption === 'name-descending')
            result.sort((a, b) =>
                String(b.product_name || '').localeCompare(String(a.product_name || ''))
            );

        return result;
    }, [
        productList,
        selectedMin,
        selectedMax,
        stockFilter,
        selectedAttributes,
        sortOption
    ]);

    const handleMinChange = value => {
        const val = Math.min(Math.max(Number(value), minPrice), selectedMax);
        setSelectedMin(val);
    };

    const handleMaxChange = value => {
        const val = Math.max(Math.min(Number(value), maxPrice), selectedMin);
        setSelectedMax(val);
    };

    const resetFilters = () => {
        setSelectedMin(minPrice);
        setSelectedMax(maxPrice);
        setStockFilter('all');
        setSortOption('default');
        setSelectedAttributes({});
    };

    const addWishlist = async productId => {
        try {
            if (!parsedTokenData) {
                navigate('/account/login');
                return;
            }

            setWishlistLoading(productId);

            const token = localStorage.getItem('muskan_token');

            const response = await fetch(
                '/wishlist/add',
                'POST',
                { product_id: productId },
                {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            );

            if (response?.data?.success) {
                toast.success(response?.data?.message || 'Added to wishlist');
            } else {
                toast.error(response?.data?.message || 'Unable to add to wishlist');
            }
        } catch (error) {
            console.error(error);
            toast.error('Unable to add to wishlist');
        } finally {
            setWishlistLoading(null);
        }
    };

    const addCart = product => {
        if (!getStock(product)) {
            toast.error('Product is out of stock');
            return;
        }

        const priceObj = getPriceObj(product);
        const price = Number(priceObj?.product_MSP || 0);

        dispatch(
            addToCart({
                attributes: product.attributes,
                product_name: product.product_name,
                product_MSP: price,
                brand_id: product.brand_id,
                category_id: product.category_id,
                gst_id: product.gst_id,
                categroy_name: product.sub_category_name,
                gst_percent: product.gst_percent,
                images: product.images,
                in_stock_status: product.in_stock_status,
                is_delete: product.is_delete,
                product_MRP: Number(priceObj?.product_MRP || 0),
                product_additional_information: product.product_additional_information,
                product_code: product.product_code,
                product_description: product.product_description,
                product_id: product.product_id,
                product_name_disp: product.product_name_disp,
                product_sku: product.product_sku,
                product_slug: product.product_slug,
                product_thumbnail: product.product_thumbnail,
                sub_category_id: product.sub_category_id,
                quantity: 1,
                subTotal: price
            })
        );

        setCartOpen(true);
    };

    const activeFilterCount =
        (stockFilter !== 'all' ? 1 : 0) +
        Object.values(selectedAttributes).reduce(
            (total, values) => total + values.length,
            0
        );

    const FilterContent = () => (
        <div className="shop-filter">
            <div className="filter-head">
                <div>
                    <span className="filter-label">SHOP</span>
                    <h5>Filters</h5>
                </div>
                <button onClick={resetFilters}>Clear All</button>
            </div>

            <div className="filter-section">
                <div className="filter-title">Availability</div>

                {[
                    ['all', 'All Products', productList.length],
                    ['in', 'In Stock', productList.filter(getStock).length],
                    ['out', 'Out of Stock', productList.filter(p => !getStock(p)).length]
                ].map(([value, label, count]) => (
                    <label className="filter-radio" key={value}>
                        <input
                            type="radio"
                            name="stock"
                            checked={stockFilter === value}
                            onChange={() => setStockFilter(value)}
                        />
                        <span>{label}</span>
                        <small>{count}</small>
                    </label>
                ))}
            </div>

            <div className="filter-section">
                <div className="filter-title">Price Range</div>

                <div className="price-inputs">
                    <div>
                        <small>MIN</small>
                        <input
                            type="number"
                            value={selectedMin}
                            min={minPrice}
                            max={selectedMax}
                            onChange={e => handleMinChange(e.target.value)}
                        />
                    </div>

                    <i>—</i>

                    <div>
                        <small>MAX</small>
                        <input
                            type="number"
                            value={selectedMax}
                            min={selectedMin}
                            max={maxPrice}
                            onChange={e => handleMaxChange(e.target.value)}
                        />
                    </div>
                </div>

                <div className="range-wrap">
                    <input
                        type="range"
                        min={minPrice}
                        max={maxPrice || 1}
                        value={selectedMin}
                        onChange={e => handleMinChange(e.target.value)}
                        className="range-slider"
                    />
                    <input
                        type="range"
                        min={minPrice}
                        max={maxPrice || 1}
                        value={selectedMax}
                        onChange={e => handleMaxChange(e.target.value)}
                        className="range-slider range-top"
                    />
                </div>

                <div className="price-labels">
                    <span>₹{minPrice.toLocaleString('en-IN')}</span>
                    <span>₹{maxPrice.toLocaleString('en-IN')}</span>
                </div>
            </div>

            {attributeGroups.map(group => (
                <div className="filter-section" key={group.attribute_id}>
                    <div className="filter-title">{group.attribute_name}</div>

                    {group.values.map(value => {
                        const checked =
                            selectedAttributes[group.attribute_id]?.includes(
                                Number(value.attribute_value_id)
                            ) || false;

                        return (
                            <label className="filter-check" key={value.attribute_value_id}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                        toggleAttribute(
                                            group.attribute_id,
                                            Number(value.attribute_value_id)
                                        )
                                    }
                                />
                                <span>{value.attribute_value}</span>
                            </label>
                        );
                    })}
                </div>
            ))}
        </div>
    );

    return (
        <>
            <ToastContainer position="top-right" autoClose={2000} />

            <section className="subcategory-page">
                <Container>
                    <div className="breadcrumb-area">
                        <Link to="/">Home</Link>
                        <span>/</span>
                        <Link to={`/${category_slug}`}>
                            {productList[0]?.category_name || 'Category'}
                        </Link>
                        <span>/</span>
                        <strong>
                            {productList[0]?.sub_category_name || 'Products'}
                        </strong>
                    </div>

                    <div className="page-top">
                        <div>
                            {/* <span className="collection-label">OUR COLLECTION</span> */}
                            <h1>
                                {productList[0]?.sub_category_name || 'Products'}
                            </h1>
                            <p>
                                Discover our carefully selected collection of premium products.
                            </p>
                        </div>

                        <div className="desktop-sort">
                            <span>{filteredProducts.length} Items</span>
                            <select
                                value={sortOption}
                                onChange={e => setSortOption(e.target.value)}
                            >
                                <option value="default">Sort: Featured</option>
                                <option value="price-low-to-high">Price: Low to High</option>
                                <option value="price-high-to-low">Price: High to Low</option>
                                <option value="discount">Best Discount</option>
                                <option value="name-ascending">Name: A to Z</option>
                                <option value="name-descending">Name: Z to A</option>
                            </select>
                        </div>
                    </div>

                    <div className="mobile-toolbar">
                        <button onClick={() => setMobileFilter(true)}>
                            ☰ Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                        </button>

                        <select
                            value={sortOption}
                            onChange={e => setSortOption(e.target.value)}
                        >
                            <option value="default">Featured</option>
                            <option value="price-low-to-high">Price Low</option>
                            <option value="price-high-to-low">Price High</option>
                            <option value="discount">Discount</option>
                            <option value="name-ascending">A - Z</option>
                            <option value="name-descending">Z - A</option>
                        </select>
                    </div>

                    {loading ? (
                        <div className="loader-box">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Row className="shop-row">
                            <Col lg={3} className="desktop-filter">
                                <FilterContent />
                            </Col>

                            <Col lg={9}>
                                {filteredProducts.length ? (
                                    <div className="product-grid">
                                        {filteredProducts.map(product => {
                                            const price = getPrice(product);
                                            const mrp = getMRP(product);
                                            const discount = getDiscount(mrp, price);
                                            const inStock = getStock(product);
                                            const image = getImage(product.product_thumbnail);

                                            const productUrl = `/${product.category_slug}/${product.sub_category_slug}/${product.product_slug}`;

                                            return (
                                                <div className="product-card" key={product.product_id}>
                                                    
                                                    <div className="product-image-wrap">
                                                        {discount > 0 && (
                                                            <span className="discount-badge">
                                                                {discount}% OFF
                                                            </span>
                                                        )}


                                                        <Link to={productUrl} className="image-link">
                                                            <img
                                                                src={image}
                                                                alt={product.product_name}
                                                                className="product-image"
                                                                onError={e => {
                                                                    e.currentTarget.src =
                                                                        '/images/default-product.jpg';
                                                                }}
                                                            />
                                                        </Link>

                                                        <span
                                                            className={`stock-badge ${
                                                                inStock ? 'in' : 'out'
                                                            }`}
                                                        >
                                                            {inStock ? 'In Stock' : 'Out of Stock'}
                                                        </span>
                                                    </div>

                                                    <div className="product-info">
                                                        <div className="product-category">
                                                            {product.category_name}
                                                        </div>

                                                        <Link
                                                            className="product-name"
                                                            to={productUrl}
                                                        >
                                                            {product.product_name_disp ||
                                                                product.product_name}
                                                        </Link>

                                                        {(product.attributes || []).length > 0 && (
                                                            <div className="attribute-list">
                                                                {(product.attributes || [])
                                                                    .slice(0, 3)
                                                                    .map(attr => (
                                                                        <span key={attr.attribute_value_id}>
                                                                            {attr.attribute_name}: {attr.attribute_value}
                                                                        </span>
                                                                    ))}
                                                            </div>
                                                        )}

                                                        <div className="product-price">
                                                            <strong>
                                                                ₹{price.toLocaleString('en-IN')}
                                                            </strong>

                                                            {mrp > price && (
                                                                <>
                                                                    <del>
                                                                        ₹{mrp.toLocaleString('en-IN')}
                                                                    </del>
                                                                    <em>{discount}% off</em>
                                                                </>
                                                            )}
                                                        </div>
                                                        <Link to={productUrl} className="image-link">

                                                        <button
                                                            className={`cart-btn ${
                                                                !inStock ? 'disabled' : ''
                                                            }`}
                                                            // onClick={() => addCart(product)}
                                                            disabled={!inStock}
                                                        >
                                                            {inStock ? (
                                                                <>
                                                                    <span>+</span> Add to Cart
                                                                </>
                                                            ) : (
                                                                'Out of Stock'
                                                            )}
                                                        </button>
                                                        </Link>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="empty-products">
                                        <div className="empty-icon">⌕</div>
                                        <h4>No Products Found</h4>
                                        <p>Try changing your filters.</p>
                                        <button onClick={resetFilters}>
                                            Reset Filters
                                        </button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    )}
                </Container>
            </section>

            <Offcanvas
                show={mobileFilter}
                onHide={() => setMobileFilter(false)}
                placement="start"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Product Filters</Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <FilterContent />

                    <button
                        className="mobile-apply-btn"
                        onClick={() => setMobileFilter(false)}
                    >
                        Apply Filters
                    </button>
                </Offcanvas.Body>
            </Offcanvas>

            <CartSidebar
                isOpen={cartOpen}
                closeSidebar={() => setCartOpen(false)}
            />

            <style>{`
                :root{--gold:#a3895b;--gold-dark:#8d754b;--black:#222;--border:#e9e5dc;--light:#faf9f6}
                .subcategory-page{padding:28px 0 65px;background:#fff;color:var(--black)}
                .breadcrumb-area{display:flex;align-items:center;gap:8px;margin-bottom:10px;font-size:12px;color:#999}
                .breadcrumb-area a{color:#888;text-decoration:none}
                .breadcrumb-area a:hover{color:var(--gold)}
                .breadcrumb-area strong{font-weight:500;color:#444}
                .collection-label{display:block;color:var(--gold);font-size:10px;font-weight:700;letter-spacing:2px;margin-bottom:6px}
                .page-top{display:flex;align-items:flex-end;justify-content:space-between;padding-bottom:7px;border-bottom:1px solid var(--border);margin-bottom:15px}
                .page-top h1{font-size:30px;line-height:1.15;font-weight:700;margin:0;letter-spacing:-.5px}
                .page-top p{font-size:13px;color:#999;margin:8px 0 0}
                .desktop-sort{display:flex;align-items:center;gap:14px}
                .desktop-sort>span{font-size:12px;color:#999}
                .desktop-sort select,.mobile-toolbar select{min-width:175px;height:40px;padding:0 32px 0 12px;border:1px solid var(--border);border-radius:6px;background:#fff;color:#444;font-size:12px;outline:0}
                .shop-row{--bs-gutter-x:24px}
                .shop-filter{border:1px solid var(--border);border-radius:8px;padding:18px;background:#fff;position:sticky;top:20px}
                .filter-head{display:flex;align-items:center;justify-content:space-between;padding-bottom:14px;border-bottom:1px solid var(--border)}
                .filter-label{font-size:9px;color:var(--gold);font-weight:700;letter-spacing:1.5px}
                .filter-head h5{font-size:17px;margin:2px 0 0;font-weight:700}
                .filter-head button{border:0;background:none;color:var(--gold-dark);font-size:11px;font-weight:600}
                .filter-section{padding:10px 0;border-bottom:1px solid var(--border)}
                .filter-section:last-child{border:0;padding-bottom:0}
                .filter-title{font-size:12px;font-weight:700;margin-bottom:8px;color:#000000; text-transform: capitalize;}
                .filter-radio,.filter-check{display:flex;align-items:center;gap:9px;margin:9px 0;font-size:14px;color:#000000;cursor:pointer}
                .filter-radio input,.filter-check input{width:14px;height:14px;accent-color:var(--gold)}
                .filter-radio small{margin-left:auto;color:#aaa;font-size:10px}
                .price-inputs{display:flex;align-items:flex-end;gap:7px}
                .price-inputs>div{flex:1}
                .price-inputs small{display:block;color:#aaa;font-size:9px;margin-bottom:4px}
                .price-inputs input{width:100%;height:34px;border:1px solid var(--border);border-radius:5px;padding:0 7px;font-size:11px;outline:0}
                .price-inputs input:focus{border-color:var(--gold)}
                .price-inputs i{font-size:10px;color:#aaa;padding-bottom:10px;font-style:normal}
                .range-wrap{height:27px;position:relative;margin:10px 0 0}
                .range-slider{position:absolute;left:0;top:5px;width:100%;height:4px;appearance:none;background:#e8e4db;pointer-events:none}
                .range-slider::-webkit-slider-thumb{appearance:none;width:15px;height:15px;border-radius:50%;background:var(--gold);border:2px solid #fff;box-shadow:0 1px 4px #aaa;pointer-events:auto;cursor:pointer}
                .range-slider::-moz-range-thumb{width:13px;height:13px;border:2px solid #fff;border-radius:50%;background:var(--gold);pointer-events:auto;cursor:pointer}
                .range-top{z-index:2;background:transparent}
                .price-labels{display:flex;justify-content:space-between;font-size:10px;color:#999}
                .product-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px}
                .product-card{background:#fff;border:1px solid var(--border);border-radius:8px;overflow:hidden;transition:.25s}
                .product-card:hover{border-color:#d8cfbd;box-shadow:0 10px 30px rgba(50,40,20,.08);transform:translateY(-3px)}
                .product-image-wrap{height:290px;background:var(--light);position:relative;overflow:hidden}
                .image-link{width:100%;height:100%;display:flex;align-items:center;justify-content:center}
                .product-image{width:100%;height:100%;object-fit:contain;padding:18px;display:block;transition:.35s}
                .product-card:hover .product-image{transform:scale(1.04)}
                .discount-badge{position:absolute;left:11px;top:11px;background:var(--gold);color:#fff;border-radius:3px;padding:5px 7px;font-size:9px;font-weight:700;letter-spacing:.3px;z-index:2}
                .wishlist-btn{position:absolute;right:11px;top:11px;width:34px;height:34px;border:1px solid #eee;border-radius:50%;background:#fff;color:#555;font-size:20px;line-height:1;z-index:3;box-shadow:0 3px 12px rgba(0,0,0,.06);transition:.2s}
                .wishlist-btn:hover{background:var(--gold);border-color:var(--gold);color:#fff}
                .wishlist-btn.loading{opacity:.6}
                .stock-badge{position:absolute;left:11px;bottom:11px;border-radius:3px;padding:4px 7px;font-size:9px;font-weight:600;z-index:2}
                .stock-badge.in{background:#eef7f0;color:#33764b}
                .stock-badge.out{background:#fff0ef;color:#bd4b45}
                .product-info{padding:14px 14px 15px}
                .product-category{font-size:9px;color:#aaa;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px}
                .product-name{display:block;color:#000000;text-decoration:none;font-size:14px;font-weight:600;line-height:1.4;text-transform: capitalize;}
                .product-name:hover{color:var(--gold-dark)}
                .attribute-list{display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 6px 0;
    text-transform: capitalize;}
                .attribute-list span{font-size:9px;background:#f7f5f0;color:#000000;border-radius:3px;padding:3px 5px}
                .product-price{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-top:8px}
                .product-price strong{font-size:16px;color:#222}
                .product-price del{font-size:11px;color:#aaa}
                .product-price em{font-size:10px;color:#4d8a60;font-style:normal;font-weight:600}
                .cart-btn{width:100%;height:38px;margin-top:12px;border:1px solid var(--gold);background:var(--gold);color:#fff;border-radius:5px;font-size:11px;font-weight:600;transition:.2s}
                .cart-btn span{font-size:15px;vertical-align:-1px;margin-right:3px}
                .cart-btn:hover{background:var(--gold-dark);border-color:var(--gold-dark)}
                .cart-btn.disabled{background:#f1f1f1;border-color:#eee;color:#aaa;cursor:not-allowed}
                .loader-box{height:420px;display:flex;align-items:center;justify-content:center;color:var(--gold)}
                .empty-products{text-align:center;padding:90px 20px;border:1px dashed #ddd;border-radius:8px}
                .empty-icon{font-size:40px;color:#aaa}
                .empty-products h4{font-size:18px;margin:8px 0 4px}
                .empty-products p{font-size:12px;color:#999}
                .empty-products button,.mobile-apply-btn{border:0;background:var(--gold);color:#fff;border-radius:5px;padding:10px 18px;font-size:11px;font-weight:600}
                .mobile-toolbar{display:none}
                .mobile-apply-btn{width:100%;height:42px;margin-top:15px}
                @media(max-width:1199px){
                    .product-image-wrap{height:260px}
                    .product-grid{gap:15px}
                }
                @media(max-width:991px){
                    .desktop-filter,.desktop-sort{display:none}
                    .mobile-toolbar{display:flex;gap:10px;margin-bottom:18px}
                    .mobile-toolbar button{flex:1;height:40px;border:1px solid var(--border);background:#fff;border-radius:6px;color:#555;font-size:12px}
                    .mobile-toolbar select{flex:1;min-width:0}
                    .product-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
                    .product-image-wrap{height:280px}
                }
                @media(max-width:575px){
                    .subcategory-page{padding:18px 0 40px}
                    .breadcrumb-area{margin-bottom:20px;font-size:10px}
                    .page-top{padding-bottom:16px;margin-bottom:15px}
                    .page-top h1{font-size:22px}
                    .page-top p{font-size:11px;max-width:270px}
                    .collection-label{font-size:8px}
                    .product-grid{gap:10px}
                    .product-image-wrap{height:190px}
                    .product-image{padding:12px}
                    .product-info{padding:10px}
                    .product-category{font-size:8px}
                    .product-name{font-size:12px;min-height:34px}
                    .attribute-list{margin:6px 0}
                    .attribute-list span{font-size:8px;padding:3px 4px}
                    .product-price{gap:5px}
                    .product-price strong{font-size:14px}
                    .product-price del{font-size:9px}
                    .product-price em{font-size:9px}
                    .cart-btn{height:35px;margin-top:9px;font-size:10px}
                    .discount-badge,.stock-badge{font-size:8px;padding:3px 5px}
                    .wishlist-btn{width:29px;height:29px;right:7px;top:7px;font-size:17px}
                }
            `}</style>
        </>
    );
};

export default SubCategory;